import { Component, computed, HostListener, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { NewsWidgetComponent } from '../../core/components/widgets/news-widget/news-widget.component';
import { RatecapComponent } from '../../core/components/widgets/ratecap/ratecap.component';
import { ContactWholesalerComponent } from '../../core/components/widgets/contact-wholesaler/contact-wholesaler.component';
import { PromotionsWidgetComponent } from '../../core/components/widgets/promotions-widget/promotions-widget.component';
import { ProductTabsComponent } from '../../core/components/widgets/products/products.component';
import { loadRemoteModule } from '@angular-architects/module-federation';
import { AuthorizationService } from '../../shared/services/authorization.service';
import { requestforEmulationPayload } from '../../shared/models/model';
import { HeaderService } from '../../shared/services/header.service';
import { CommonModule } from '@angular/common';
import config from '../../../assets/config.json';
import { switchAdvisorService } from '../../shared/services/switchAdvisorService';

/**
 * DashboardComponent is the main component for the dashboard page.
 * It imports various widget components and dynamically loads a remote module.
 *
 * @selector app-dashboard
 * @standalone true
 * @imports [
 *   NewsWidgetComponent,
 *   RatecapComponent,
 *   ContactWholesalerComponent,
 *   PromotionsWidgetComponent,
 *   ProductTabsComponent
 * ]
 * @templateUrl ./dashboard.component.html
 * @styleUrl ./dashboard.component.scss
 */
@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    NewsWidgetComponent,
    RatecapComponent,
    ContactWholesalerComponent,
    PromotionsWidgetComponent,
    ProductTabsComponent,
    CommonModule,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  /**
   * Tabs property to hold tab data.
   */
  tabs: any;
  token: string = '';
  isAuthorized: Boolean = false;
  switchedAdvisor = computed(() => this._switchAdvisorService.switchedAdvisor());

  /**
   * ViewContainerRef to dynamically load the ParentComponent.
   */
  @ViewChild('WidgetContractsComponent', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;
  windowIsSmall: boolean = false;

  constructor(
    private _authservice: AuthorizationService,
    private _headerService: HeaderService,
    private _switchAdvisorService: switchAdvisorService,
  ) {
    this._authservice.getAccessToken().then((token) => {
      this.token = token;
    });
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Dynamically loads the ParentComponent from a remote module.
   */
  /** Commenting for BOB */
  async ngOnInit() {
    await this.checkAuthorization();
    if (this.isAuthorized) {
      const { WidgetContractsComponent } = await loadRemoteModule({
        type: 'module',
        remoteEntry: config.bobRemoteURL,
        exposedModule: './WidgetContractsComponent',
      });
      const componentFactory: any = this.container.createComponent(WidgetContractsComponent);
      componentFactory.instance.token = this.token;
      componentFactory.instance.roles = this._authservice.getFormattedGroups();
      componentFactory.instance.switchedAdvisor = JSON.stringify(this.switchedAdvisor());
      this.container.insert(componentFactory.hostView);
    }
    this.checkScreenSize();
  }

  async checkAuthorization() {
    this.isAuthorized = this._headerService.hasBOBAccess();
    if (!this.isAuthorized) {
      const payload: requestforEmulationPayload = {
        requestingSystem: 'FinPro',
        isEmulating: false,
        emulatingUserId: '',
      };
      try {
        const data = await this._headerService.getNavigationHeader(payload)?.toPromise();
        if (data) {
          this.isAuthorized = this._headerService.hasBookOfBusiness(data);
        }
      } catch (error) {
        console.error('Error fetching navigation header:', error);
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(_event: Event) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    // check if screen width is less than 969px
    this.windowIsSmall = window.innerWidth < 969;
  }
}
